import { InvoicesListAddressCell } from '@app/components/invoices/InvoicesList';
import { InvoiceStatusLabel } from '@app/components/invoices/InvoiceStatusLabel';
import { Button } from '@app/components/various/Button';
import { Download } from '@app/components/various/Icons';
import { Link as LinkComponent } from '@app/components/various/Link';
import { StatusCell } from '@app/components/various/Table';
import { Tooltip } from '@app/components/various/Tooltip';
import { getCalculatedInvoiceStatus, getIsInvoiceStandard } from '@app/logic/invoices';
import { compiledPaths } from '@app/paths';
import { useInvoiceDownload } from '@app/services/hooks/invoice/useInvoiceDownload';
import { isDefined } from '@app/utils/is';
import { renderStandardCell } from '@app/utils/table';
import { createColumnHelper, getCoreRowModel } from '@tanstack/react-table';
import { Invoice } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { usePaginationHandler } from '../table/usePaginationHandler';
import { useTable } from '../table/useTable';
import { useTableColumnsConfiguration } from '../table/useTableColumnsConfiguration';

const columnHelper = createColumnHelper<Invoice.AnyInvoice>();

interface Props {
  page: number;
  invoices: Invoice.AnyInvoice[];
  handlePageChange: (page: number) => void;
}

export const useInvoicesListTable = ({ page, invoices, handlePageChange }: Props) => {
  const { t } = useTranslation(['invoices', 'orders', 'totals', 'common']);
  const { columnsVisibility } = useTableColumnsConfiguration({ tableName: 'invoicesTable' });
  const { downloadInvoice } = useInvoiceDownload();
  const { pagination, handlePaginationChange } = usePaginationHandler({ onPageChange: handlePageChange, page });

  const handleDownloadClick = React.useCallback(
    (invoice: Invoice.Standard) => () => {
      const { invoiceHash, invoice: invoiceId, invoiceNumber } = invoice;

      downloadInvoice({ invoiceHash, invoiceId, invoiceNumber });
    },
    [downloadInvoice],
  );

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('status', {
        cell: info => {
          const status = getCalculatedInvoiceStatus(info.row.original);

          return <StatusCell status={status} tooltip={<InvoiceStatusLabel status={status} />} />;
        },
        enableSorting: false,
        header: t('common:status'),
        meta: {
          align: 'center',
          noWrap: true,
          width: 50,
        },
      }),
      columnHelper.accessor('invoiceNumber', {
        cell: info => <span className="body-1">{renderStandardCell(info)}</span>,
        enableHiding: false,
        header: t('invoices:invoice_no'),
      }),
      columnHelper.accessor('orderNumber', {
        cell: info => {
          const invoice = info.row.original;

          if (!getIsInvoiceStandard(invoice)) {
            return null;
          }

          if (!isDefined(invoice.order)) {
            return '-';
          }

          const path = compiledPaths.CHECKOUT_SUCCESS({ id: invoice.order });

          return (
            <LinkComponent to={path} className="z-interactiveTableItem">
              {invoice.orderNumber}
            </LinkComponent>
          );
        },
        enableHiding: false,
        header: t('orders:order_no'),
      }),
      columnHelper.accessor('address', {
        cell: info => <InvoicesListAddressCell invoice={info.row.original} />,
        header: t('invoices:invoice_address'),
        meta: {
          width: '100%',
        },
      }),
      columnHelper.accessor('createdAt', {
        cell: renderStandardCell,
        header: t('common:created'),
        meta: {
          align: 'left',
          noWrap: true,
        },
      }),
      columnHelper.accessor('dueDate', {
        cell: renderStandardCell,
        header: t('common:due_date'),
        meta: {
          align: 'left',
          noWrap: true,
        },
      }),
      columnHelper.accessor('grandTotalWithoutTax', {
        cell: renderStandardCell,
        header: t('totals:total_no_tax'),
        meta: {
          align: 'right',
        },
      }),
      columnHelper.accessor('grandTotal', {
        cell: renderStandardCell,
        header: t('totals:total_with_tax'),
        meta: {
          align: 'right',
        },
      }),
      columnHelper.accessor('totalDue', {
        cell: renderStandardCell,
        header: t('totals:total_due_with_tax'),
        meta: {
          align: 'right',
        },
      }),
      columnHelper.display({
        cell: info => {
          const invoice = info.row.original;

          if (!getIsInvoiceStandard(invoice) || !invoice.isPayable) {
            return null;
          }

          const path = compiledPaths.INVOICE_DETAILS({ invoiceHash: invoice.invoiceHash, invoiceId: invoice.invoice });

          return (
            <Button as={Link} className="absolute-cursor-pointer" to={path} size="medium">
              {t('invoices:pay_invoice')}
            </Button>
          );
        },
        enableHiding: false,
        id: 'pay',
        meta: {
          skeletonType: 'button',
        },
      }),
      columnHelper.display({
        cell: info => {
          const invoice = info.row.original;

          if (!getIsInvoiceStandard(invoice)) {
            return null;
          }

          return (
            <Tooltip content={t('invoices:download_invoice')}>
              <Button
                color="secondary"
                variant="text"
                icon={Download}
                className="z-interactiveTableItem"
                aria-label={t('invoices:download_invoice')}
                onClick={handleDownloadClick(invoice)}
                size="medium"
              />
            </Tooltip>
          );
        },
        enableHiding: false,
        header: t('common:option_other'),
        id: 'download',
        meta: {
          align: 'center',
          skeletonType: 'button',
        },
      }),
    ],
    [handleDownloadClick, t],
  );

  const table = useTable(
    {
      columns,
      data: invoices,
      enableSorting: false,
      getCoreRowModel: getCoreRowModel(),
      manualPagination: true,
      onPaginationChange: handlePaginationChange,
      state: {
        pagination,
      },
    },
    columnsVisibility,
  );

  return {
    table,
  };
};
